import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ReactTooltip from 'react-tooltip';
import { CSSTransition } from 'react-transition-group';
import { useIntl, FormattedMessage } from 'react-intl';

import {
  useReady,
  imgLinks,
  GET_CUSTOMER,
  DENTOLO_REFERRAL_URL,
  JOIN_REFERRAL_PROGRAM,
} from '../../../shared';

import { Container, PageTitle, Separator } from '../../../components/common';
import { Button, ErrorPlaceholder, Loading, Paragraph } from '../../../components/ui';
import {
  StyledPresentImageContainer,
  StyledSocialLinksContainer,
  StyledSocialGroup,
  StyledSocialImg,
  StyledReferralItemContainer,
  StyledReferralItemIndexContainer,
  StyledReferralItemIndex,
  StyledReferralItemDescriptionContainer,
  StyledReferralItemDescription,
  StyledReferralCard,
  StyledReferralGiftText,
  StyledReferralCardContent,
  StyledReferralInput,
  StyledReferralCoverImageContainerSm,
  StyledReferralCoverImageContainerLg,
} from './ReferralInfoPage.styled';

const referralDescriptionItems = [
  {
    id: 0,
    title: <FormattedMessage id="referral.page.bulletpoint.one.title" />,
    description: <FormattedMessage id="referral.page.bulletpoint.one.description" />,
  },
  {
    id: 1,
    title: <FormattedMessage id="referral.page.bulletpoint.two.title" />,
    description: (
      <FormattedMessage
        id="referral.page.bulletpoint.two.description"
        values={{ brand: 'Amazon' }}
      />
    ),
  },
];

const shareSocialItems = [
  {
    id: 'fb',
    image: imgLinks[`icon.user.referral.fb`],
    click: (refUrl) => {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refUrl)}`,
        'pop',
        'width=600, height=400, scrollbars=no'
      );
    },
  },
  {
    id: 'whatsapp',
    image: imgLinks[`icon.user.referral.whatsapp`],
    click: (refUrl, intl) => {
      const generateWhatsappMessage = (refUrl) =>
        intl.formatMessage(
          {
            id: 'referral.page.message.whatsapp',
          },
          {
            referralUrl: refUrl,
          }
        );
      const whatsappBody = (referralUrl) => generateWhatsappMessage(referralUrl);
      const messageText = whatsappBody(encodeURIComponent(refUrl));
      const URL = `whatsapp://send?text=${messageText}`;
      window.open(URL, '_blank');
    },
  },
  {
    id: 'email',
    image: imgLinks[`icon.user.referral.email`],
    click: (refUrl, intl) => {
      const generateEmailMessage = (refUrl) =>
        intl.formatMessage(
          {
            id: 'referral.page.message.email',
          },
          {
            referralUrl: refUrl,
          }
        );
      const emailText = (referralUrl) => generateEmailMessage(referralUrl);
      const emailAddress = '';
      const emailSubject = 'Dentolo Referral';
      const emailBody = emailText(encodeURIComponent(refUrl));
      window.open(`mailto:${emailAddress}?subject=${emailSubject}&body=${emailBody}`);
    },
  },
];

const ReferralDescriptionItem = ({ id, title, description }) => {
  return (
    <>
      <StyledReferralItemContainer>
        <StyledReferralItemIndexContainer>
          <StyledReferralItemIndex style={{ right: id === 0 ? '0.5px' : '0px' }}>
            {++id}
          </StyledReferralItemIndex>
        </StyledReferralItemIndexContainer>
        <StyledReferralItemDescriptionContainer>
          <h3>{title}</h3>
          <StyledReferralItemDescription>{description}</StyledReferralItemDescription>
        </StyledReferralItemDescriptionContainer>
      </StyledReferralItemContainer>
    </>
  );
};

const DentoloReferralInfoPage = () => {
  const intl = useIntl();
  const transitionTimeout = 500;
  const ready = useReady();
  const tooltipNode = useRef(null);
  const [refUrl, setRefUrl] = useState(DENTOLO_REFERRAL_URL);

  const { data: customerData, loading: customerLoading } = useQuery(GET_CUSTOMER);
  const customer = customerData?.customer;
  const refId = customer?.referralCode;

  const [
    generateDentalReferralCode,
    { loading: referralCodeLoading, error: referralCodeGenerationError },
  ] = useMutation(JOIN_REFERRAL_PROGRAM, {
    refetchQueries: [{ query: GET_CUSTOMER }],
  });

  useEffect(() => {
    const hasReferralCode = !!customer?.referralCode;

    if (!hasReferralCode) {
      generateDentalReferralCode();
    }
  }, [customer, customerLoading, generateDentalReferralCode]);

  useEffect(() => {
    if (refId && refUrl.indexOf('&refid=') < 0) {
      setRefUrl(`${refUrl}&refid=${refId}`);
    }
  }, [refId, refUrl]);

  const onLinkCopy = () => {
    if (!!window?.navigator?.clipboard) {
      window.navigator.clipboard.writeText(refUrl).then(
        () => {
          if (tooltipNode) {
            ReactTooltip.show(tooltipNode.current);
            setTimeout(() => {
              ReactTooltip.hide(tooltipNode.current);
            }, 1000);
          }
        },
        (err) => console.error('Could not copy text: ', err)
      );
    }
  };

  if (referralCodeLoading) return <Loading showLogo />;

  if ((!customerLoading && !customer) || referralCodeGenerationError) return <ErrorPlaceholder />;

  if (!customerLoading && !!customer) {
    return (
      <Container>
        <CSSTransition in={ready} timeout={transitionTimeout} classNames="fade" unmountOnExit>
          <>
            <PageTitle>
              <FormattedMessage id="referral.page.title" />
            </PageTitle>

            <StyledReferralCoverImageContainerSm>
              <img
                src={imgLinks['image.cover.referral.dental.sm']}
                alt="dental referral cover small"
              />
            </StyledReferralCoverImageContainerSm>

            <StyledReferralCoverImageContainerLg>
              <img
                src={imgLinks['image.cover.referral.dental.lg']}
                alt="dental referral cover large"
              />
            </StyledReferralCoverImageContainerLg>
          </>
        </CSSTransition>

        <CSSTransition in={ready} timeout={transitionTimeout} classNames="fade" unmountOnExit>
          <StyledReferralCard>
            <StyledReferralGiftText>
              <StyledPresentImageContainer>
                <img src={imgLinks['icon.user.referral.present']} alt="" />
              </StyledPresentImageContainer>
              <FormattedMessage id="referral.page.content" values={{ contract: 'dentolo' }} />
            </StyledReferralGiftText>
            <Separator />
            <StyledReferralCardContent
              data-for="link-copied"
              data-tip
              data-event="none"
              ref={tooltipNode}
            >
              <StyledReferralInput
                id="referral-link"
                name="referral-link"
                type="text"
                value={refUrl}
                onChange={(e) => setRefUrl(e.target.value)}
                readOnly
              />
              <Button
                variant="primary"
                onClick={onLinkCopy}
                className="u3-referral-input-group-btn"
              >
                <FormattedMessage id="referral.page.copy.button" />
              </Button>
              <ReactTooltip id="link-copied" className="u3-referral-tooltip" effect="solid">
                <FormattedMessage id="referral.page.link.copied" />
              </ReactTooltip>
            </StyledReferralCardContent>
            <Separator />

            <StyledSocialLinksContainer>
              <StyledSocialGroup>
                {shareSocialItems.map((socialItem) => (
                  <StyledSocialImg
                    key={socialItem.id}
                    src={socialItem.image}
                    alt={socialItem.id}
                    onClick={() => socialItem.click(refUrl, intl)}
                  />
                ))}
              </StyledSocialGroup>
            </StyledSocialLinksContainer>
          </StyledReferralCard>
        </CSSTransition>

        <CSSTransition in={ready} timeout={transitionTimeout} classNames="fade" unmountOnExit>
          <StyledReferralCard>
            <StyledReferralCardContent style={{ alignItems: 'start' }}>
              {referralDescriptionItems.map((descriptionItem) => (
                <ReferralDescriptionItem key={descriptionItem.id} {...descriptionItem} />
              ))}
            </StyledReferralCardContent>
            <Separator />

            <Paragraph textAlign="center">
              <FormattedMessage id="referral.page.terms_and_conditions.text" />
              &nbsp;
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://versicherung.dentolo.de/freunde-werben-teilnahmebedingungen/"
              >
                <FormattedMessage id="referral.page.terms_and_conditions.link_text" />
              </a>
            </Paragraph>
          </StyledReferralCard>
        </CSSTransition>
      </Container>
    );
  } else {
    return <></>;
  }
};

export default DentoloReferralInfoPage;
